import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plus,
  Pencil, 
  Trash2,
  Search,
  RefreshCw,
  User as UserIcon,
  BookOpen,
  TrendingUp,
  Activity,
  UserCheck,
  UserPlus
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Instructor } from '@/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Helmet } from 'react-helmet-async';
import { Progress } from '@/components/ui/progress';

// User interface from the users table
interface User {
  id: string;
  email: string;
  created_at: string;
  role: string;
  is_active: boolean;
  profile_image_file_url?: string;
}

interface InstructorSummary {
  totalInstructors: number;
  availableForInstructor: number;
  totalExpertise: number;
}

const Instructors = () => {
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentInstructor, setCurrentInstructor] = useState<Instructor | null>(null);
  
  // Form states
  const [selectedUserId, setSelectedUserId] = useState('');
  const [formBio, setFormBio] = useState('');
  const [formExpertise, setFormExpertise] = useState('');

  const [summaryData, setSummaryData] = useState<InstructorSummary>({
    totalInstructors: 0,
    availableForInstructor: 0,
    totalExpertise: 0,
  });

  useEffect(() => {
    fetchInstructors();
    fetchUsers();
    fetchInstructorSummary();
  }, []);

  useEffect(() => {
    // Update available users whenever instructors or users change
    if (users.length > 0 && instructors.length >= 0) {
      // Filter out users that are already instructors
      const instructorEmails = instructors.map(instructor => instructor.email);
      const filtered = users.filter(user => 
        !instructorEmails.includes(user.email) && user.is_active
      );
      setAvailableUsers(filtered);
      setSummaryData(prev => ({ ...prev, availableForInstructor: filtered.length }));
    }
  }, [users, instructors]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, created_at, is_active, profile_image_file_url')
        .order('email');

      if (error) throw error;
      
      setUsers(data || []);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      toast.error('Não foi possível carregar os usuários');
    }
  };

  const fetchInstructors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('instructors')
        .select('*')
        .order('first_name');

      if (error) throw error;
      
      setInstructors(data);
    } catch (error) {
      console.error('Erro ao buscar instrutores:', error);
      toast.error('Não foi possível carregar os instrutores');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstructorSummary = async () => {
    try {
      const { count: totalInstructors, error: countError } = await supabase
        .from('instructors')
        .select('*', { count: 'exact', head: true });
      if (countError) throw countError;

      const { data: expertiseData, error: expertiseError } = await supabase
        .from('instructors')
        .select('expertise');
      if (expertiseError) throw expertiseError;
      
      const distinctExpertise = new Set(expertiseData.map(item => item.expertise).filter(Boolean));

      setSummaryData(prev => ({
        ...prev,
        totalInstructors: totalInstructors || 0,
        totalExpertise: distinctExpertise.size
      }));
    } catch (error) {
      console.error('Error fetching instructor summary:', error);
    }
  };

  const handleSearch = () => {
    const filtered = instructors.filter(instructor => 
      instructor.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instructor.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instructor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (instructor.expertise && instructor.expertise.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setInstructors(filtered);
  };

  const openAddDialog = () => {
    setSelectedUserId('');
    setFormBio('');
    setFormExpertise('');
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (instructor: Instructor) => {
    setCurrentInstructor(instructor);
    setFormBio(instructor.bio || '');
    setFormExpertise(instructor.expertise || '');
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (instructor: Instructor) => {
    setCurrentInstructor(instructor);
    setIsDeleteDialogOpen(true);
  };

  const handleAddInstructor = async () => {
    if (!selectedUserId) {
      toast.error('Selecione um usuário para adicionar como instrutor');
      return;
    }

    try {
      // Get user details from selected user
      const selectedUser = users.find(user => user.id === selectedUserId);
      if (!selectedUser) {
        toast.error('Usuário selecionado não encontrado');
        return;
      }

      // Extract first and last name from email if not available
      let firstName = selectedUser.email.split('@')[0];
      let lastName = '';
      
      if (firstName.includes('.')) {
        const nameParts = firstName.split('.');
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' ');
        // Capitalize
        firstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
        lastName = lastName.charAt(0).toUpperCase() + lastName.slice(1);
      }

      const { data, error } = await supabase
        .from('instructors')
        .insert([
          { 
            first_name: firstName,
            last_name: lastName,
            email: selectedUser.email,
            bio: formBio || null,
            expertise: formExpertise || null,
            profile_picture: selectedUser.profile_image_file_url || null,
          }
        ])
        .select();

      if (error) throw error;

      toast.success('Instrutor criado com sucesso');
      setIsAddDialogOpen(false);
      fetchInstructors();
      fetchInstructorSummary();
    } catch (error) {
      console.error('Erro ao criar instrutor:', error);
      toast.error('Não foi possível criar o instrutor');
    }
  };

  const handleEditInstructor = async () => {
    if (!currentInstructor) {
      toast.error('Instrutor não encontrado');
      return;
    }

    try {
      const { error } = await supabase
        .from('instructors')
        .update({ 
          bio: formBio || null,
          expertise: formExpertise || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentInstructor.id);

      if (error) throw error;

      toast.success('Instrutor atualizado com sucesso');
      setIsEditDialogOpen(false);
      fetchInstructors();
      fetchInstructorSummary();
    } catch (error) {
      console.error('Erro ao atualizar instrutor:', error);
      toast.error('Não foi possível atualizar o instrutor');
    }
  };

  const handleDeleteInstructor = async () => {
    if (!currentInstructor) return;

    try {
      const { count, error: countError } = await supabase
        .from('classes')
        .select('*', { count: 'exact', head: true })
        .eq('instructor_id', currentInstructor.id);
      
      if (countError) throw countError;
      
      if (count && count > 0) {
        toast.error(`Não é possível excluir este instrutor pois está associado a ${count} formações`);
        setIsDeleteDialogOpen(false);
        return;
      }

      const { error } = await supabase
        .from('instructors')
        .delete()
        .eq('id', currentInstructor.id);

      if (error) throw error;

      toast.success('Instrutor excluído com sucesso');
      setIsDeleteDialogOpen(false);
      fetchInstructors();
      fetchInstructorSummary();
    } catch (error) {
      console.error('Erro ao excluir instrutor:', error);
      toast.error('Não foi possível excluir o instrutor');
    }
  };

  const resetSearch = () => {
    setSearchTerm('');
    fetchInstructors();
  };

  const filteredInstructors = instructors.filter(instructor => 
    instructor.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instructor.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instructor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (instructor.expertise && instructor.expertise.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Instrutores</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Header with Gradient Background */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700">
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <UserIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Instrutores</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Gerencie os instrutores disponíveis para ministrar formações
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  size="default" 
                  onClick={openAddDialog}
                  className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                  disabled={availableUsers.length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Instrutor
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-6 -mt-4 relative z-10">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-md">
                        <UserIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Total de Instrutores</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.totalInstructors}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Profissionais cadastrados
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded-md">
                        <UserPlus className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Disponíveis</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.availableForInstructor}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Usuários que podem ser instrutores
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-purple-100 rounded-md">
                        <BookOpen className="h-4 w-4 text-purple-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Áreas de Expertise</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.totalExpertise}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Especialidades distintas
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Buscar instrutores..."
                      className="pl-8 h-10 bg-white border-gray-200"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <Button variant="outline" onClick={resetSearch} className="h-10">
                    <RefreshCw className="h-4 w-4 mr-2" /> Limpar
                  </Button>
                </div>
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Especialidade</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredInstructors.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-10">
                              <div className="text-muted-foreground">Nenhum instrutor encontrado</div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredInstructors.map((instructor) => (
                            <TableRow key={instructor.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={instructor.profile_picture || ''} alt={`${instructor.first_name} ${instructor.last_name}`} />
                                    <AvatarFallback>{instructor.first_name.charAt(0) + instructor.last_name.charAt(0)}</AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div className="font-extrabold">{instructor.first_name} {instructor.last_name}</div>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>{instructor.email}</TableCell>
                              <TableCell>{instructor.expertise || "-"}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(instructor)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => openDeleteDialog(instructor)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="h-16" />
        </div>
      
      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader className="space-y-3 pb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <UserPlus className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-extrabold">Adicionar Novo Instrutor</DialogTitle>
                <DialogDescription className="text-base mt-1">
                  Selecione um usuário existente para promovê-lo a instrutor.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="userId" className="text-sm font-extrabold">Usuário a ser promovido</Label>
              {availableUsers.length === 0 ? (
                <div className="text-sm text-muted-foreground p-4 bg-muted rounded-md">
                  Não há usuários disponíveis. Todos os usuários ativos já são instrutores.
                </div>
              ) : (
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="h-11 text-base">
                    <SelectValue placeholder="Selecione um usuário da lista" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.map(user => (
                      <SelectItem key={user.id} value={user.id} className="py-2">
                        {user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-3">
              <Label htmlFor="bio" className="text-sm font-extrabold">Biografia</Label>
              <Textarea 
                id="bio" 
                placeholder="Descreva brevemente o instrutor..." 
                value={formBio}
                onChange={(e) => setFormBio(e.target.value)}
                className="min-h-[100px] text-base"
              />
            </div>
            <div className="space-y-3">
              <Label htmlFor="expertise" className="text-sm font-extrabold">Área de Expertise</Label>
              <Input 
                id="expertise" 
                placeholder="Ex: Finanças, Marketing Digital, etc." 
                value={formExpertise}
                onChange={(e) => setFormExpertise(e.target.value)}
                className="h-11 text-base"
              />
            </div>
          </div>
          <DialogFooter className="pt-8 gap-3">
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} className="h-11 px-6">
              Cancelar
            </Button>
            <Button onClick={handleAddInstructor} disabled={availableUsers.length === 0 || !selectedUserId} className="h-11 px-8">
              Adicionar Instrutor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader className="space-y-3 pb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Pencil className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-extrabold">Editar Instrutor</DialogTitle>
                <DialogDescription className="text-base mt-1">
                  Atualize as informações de {currentInstructor?.first_name} {currentInstructor?.last_name}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-3">
              <Label className="text-sm font-extrabold">Usuário Vinculado</Label>
              <div className="flex items-center h-11 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                {currentInstructor?.email}
              </div>
            </div>
            <div className="space-y-3">
              <Label htmlFor="edit-bio" className="text-sm font-extrabold">Biografia</Label>
              <Textarea 
                id="edit-bio" 
                placeholder="Biografia do instrutor" 
                value={formBio}
                onChange={(e) => setFormBio(e.target.value)}
                className="min-h-[100px] text-base"
              />
            </div>
            <div className="space-y-3">
              <Label htmlFor="edit-expertise" className="text-sm font-extrabold">Área de Expertise</Label>
              <Input 
                id="edit-expertise" 
                placeholder="Especialidade do instrutor" 
                value={formExpertise}
                onChange={(e) => setFormExpertise(e.target.value)}
                className="h-11 text-base"
              />
            </div>
          </div>
          <DialogFooter className="pt-8 gap-3">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="h-11 px-6">
              Cancelar
            </Button>
            <Button onClick={handleEditInstructor} className="h-11 px-8">Salvar Alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso excluirá permanentemente o instrutor
              <span className="font-extrabold"> {currentInstructor ? `${currentInstructor.first_name} ${currentInstructor.last_name}` : ''}</span> do sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInstructor} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Instructors; 